import { NgClass } from '@angular/common';
import {
  Component,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';

export interface TypeOption {
  type: 'insight' | 'generic' | 'comparison' | 'compliance' | 'creative';
  labelKey: string;
}

interface TypeDropdownOption extends fromSeriousSystem.DropdownMenuOption {
  type: TypeOption['type'];
}

let nextUniqueId = 0;

export const TypeOptions: Record<TypeOption['type'], TypeOption> = {
  insight: {
    type: 'insight',
    labelKey:
      'ASSISTANTS.ASSISTANT_BUILDER.TYPE_SELECTOR.DROPDOWN_OPTIONS.TRAIT_INSIGHT',
  },
  generic: {
    type: 'generic',
    labelKey:
      'ASSISTANTS.ASSISTANT_BUILDER.TYPE_SELECTOR.DROPDOWN_OPTIONS.TRAIT_GENERIC',
  },
  comparison: {
    type: 'comparison',
    labelKey:
      'ASSISTANTS.ASSISTANT_BUILDER.TYPE_SELECTOR.DROPDOWN_OPTIONS.TRAIT_COMPARISON',
  },
  compliance: {
    type: 'compliance',
    labelKey:
      'ASSISTANTS.ASSISTANT_BUILDER.TYPE_SELECTOR.DROPDOWN_OPTIONS.TRAIT_COMPLIANCE',
  },
  creative: {
    type: 'creative',
    labelKey:
      'ASSISTANTS.ASSISTANT_BUILDER.TYPE_SELECTOR.DROPDOWN_OPTIONS.TRAIT_CREATIVE',
  },
};

@Component({
  selector: 'squadbox-assistant-builder-type-selector',
  imports: [
    TranslateModule,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    NgClass,
  ],
  template: `
    <!-- The row wrapper (flex gap-6 py-6) and this column's width (Type is a
         fixed w-[340px] shrink-0; the Model selector grows to fill) are applied by
         the parent page (assistant-builder.page.ts) — do not add them here. -->
    <div class="flex flex-col gap-2">
      <label
        [for]="buttonId"
        class="typo-caption font-semibold leading-4 h-5 self-start px-1 py-0.5"
        [ngClass]="{
          'text-neutral-400': disabled(),
          'text-neutral-700': !disabled(),
        }"
      >
        {{
          'ASSISTANTS.ASSISTANT_BUILDER.TYPE_SELECTOR.DROPDOWN_LABEL'
            | translate
        }}
        <span class="text-primary-500"> *</span>
      </label>
      <button
        #typeButton
        [id]="buttonId"
        class="
          group
          flex flex-row gap-2 items-center
          bg-shades-white rounded-lg px-3 py-2 h-12
          border border-neutral-200 hover:border-primary-300 focus:outline-hidden
          disabled:bg-neutral-100 disabled:border-neutral-200 disabled:hover:border-neutral-200 disabled:pointer-events-none
        "
        [sdDropdownMenuTrigger]="typeDropdown"
        [disabled]="disabled()"
      >
        <span
          class="typo-p3 group-disabled:text-neutral-400 text-left grow min-w-0 truncate"
          >{{ selectedType().labelKey | translate }}</span
        >
        <svg
          [sdUseArrowIcon]="
            typeDropdownMenuOpened() ? 'chevron-small-up' : 'chevron-small-down'
          "
          class="size-6 shrink-0 group-disabled:text-neutral-400"
        ></svg>
      </button>
    </div>

    <sd-dropdown-menu
      #typeDropdown
      width="trigger"
      [padded]="true"
      [options]="typeDropdownMenuOptions()"
      (opened)="typeDropdownMenuOpened.set(true)"
      (closed)="typeDropdownMenuOpened.set(false)"
    />
  `,
})
export class AssistantBuilderTypeSelectorComponent {
  private readonly translate = inject(TranslateService);

  protected readonly buttonId = `assistant-builder-type-selector-button-${nextUniqueId++}`;

  public readonly disabled = input<boolean>(false);
  public readonly selectedType = model.required<TypeOption>();

  public readonly typeDropdownMenuOpened = signal(false);
  public readonly typeDropdownMenuOptions = computed(
    (): TypeDropdownOption[] => {
      const selected = this.selectedType().type;
      return (Object.keys(TypeOptions) as TypeOption['type'][]).map((key) => {
        const option = TypeOptions[key];
        return {
          type: option.type,
          label: this.translate.instant(option.labelKey) as string,
          active: selected === option.type,
          action: () => this.selectedType.set(option),
        };
      });
    },
  );
}
